import React, { useState } from 'react';
import { Box, Typography, Switch, FormControlLabel, Skeleton, FormControl, InputLabel, Select, MenuItem, Collapse } from '@mui/material';
import AssignmentLateIcon from '@mui/icons-material/AssignmentLate';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PatientNarrativeCard from './pendingNotes/PatientNarrativeCard';
import type { PendingNotesBriefingProps } from './pendingNotes/types';

function comparePatientsByLastName(a: { patient_name: string }, b: { patient_name: string }) {
  const lastName = (name: string) => name.trim().split(/\s+/).slice(-1)[0] || name;
  return lastName(a.patient_name).localeCompare(lastName(b.patient_name)) ||
    a.patient_name.localeCompare(b.patient_name);
}

/**
 * Rich narrative overview for the guided_pending workflow sidebar.
 *
 * Mirrors the visual style of DashboardBriefingSidebar / BriefingSessionCard
 * but renders pending notes data (patients with undocumented sessions)
 * instead of today's enriched timeline.
 *
 * Renders:
 *   1. Summary header with segmented progress bar (signed / drafted / not started)
 *   2. Per-patient narrative cards with session details
 *   3. Clickable "Write Note" buttons for each session
 */
export default function PendingNotesBriefing({
  guidedWorkflowData,
  dashboardData,
  currentEncounter,
  onNoteClick,
}: PendingNotesBriefingProps) {
  const [showFullNames, setShowFullNames] = useState(true);
  const [sortOrder, setSortOrder] = useState<'date_asc' | 'date_desc' | 'client'>('client');

  // Guard BEFORE destructuring to avoid TypeError on null/undefined
  if (!guidedWorkflowData || !guidedWorkflowData.notes) {
    return <PendingNotesSkeleton />;
  }

  const { notes, total_count, _fallbackFromToday } = guidedWorkflowData;

  // Filter out the "Other session..." placeholder entry
  const realPatients = notes.filter(p => p.patient_id !== 'other_session');
  const sortedPatients = (() => {
    const sorted = realPatients.map((patient) => ({
      ...patient,
      sessions: [...patient.sessions].sort((a, b) => {
        const dateCompare = `${a.date} ${a.time || ''}`.localeCompare(`${b.date} ${b.time || ''}`);
        return sortOrder === 'date_desc' ? -dateCompare : dateCompare;
      }),
    }));

    if (sortOrder === 'client') {
      return sorted.sort(comparePatientsByLastName);
    }

    return sorted.sort((a, b) => {
      const aDate = a.sessions[0] ? `${a.sessions[0].date} ${a.sessions[0].time || ''}` : '';
      const bDate = b.sessions[0] ? `${b.sessions[0].date} ${b.sessions[0].time || ''}` : '';
      const comparison = aDate.localeCompare(bDate);
      return sortOrder === 'date_desc' ? -comparison : comparison;
    });
  })();

  const dateGroups = (() => {
    const groups = new Map<string, typeof realPatients>();
    realPatients.forEach((patient) => {
      patient.sessions.forEach((session) => {
        const group = groups.get(session.date) || [];
        const existing = group.find(item => item.patient_id === patient.patient_id);
        if (existing) {
          existing.sessions.push(session);
        } else {
          group.push({ ...patient, sessions: [session] });
        }
        groups.set(session.date, group);
      });
    });

    return [...groups.entries()]
      .sort(([a], [b]) => sortOrder === 'date_desc' ? b.localeCompare(a) : a.localeCompare(b))
      .map(([date, patients]) => ({
        date,
        patients: patients.sort(comparePatientsByLastName),
      }));
  })();

  // Calculate progress segments
  const signedCount = realPatients.reduce(
    (sum, p) => sum + p.sessions.filter(s => s.status === 'signed').length,
    0,
  );
  const draftedCount = realPatients.reduce(
    (sum, p) => sum + p.sessions.filter(s => s.status === 'drafted').length,
    0,
  );
  const notStartedCount = Math.max(0, total_count - signedCount - draftedCount);
  const signedPct = total_count > 0 ? Math.round((signedCount / total_count) * 100) : 0;
  const draftedPct = total_count > 0 ? Math.round((draftedCount / total_count) * 100) : 0;

  // Count unique patients with pending sessions
  const patientsWithPending = realPatients.filter(p =>
    p.sessions.some(s => s.status !== 'signed'),
  ).length;

  if (realPatients.length === 0) {
    return (
      <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No pending notes found.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {/* 1. Summary header */}
      <Box
        sx={{
          px: 2,
          py: 2,
          background:
            'linear-gradient(135deg, rgba(var(--c1-rgb, 87, 189, 228), 0.06) 0%, var(--c4-amber-bg, rgba(245,158,11,0.04)) 100%)',
          borderBottom: '1px solid var(--tile-border, #e5e7eb)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <AssignmentLateIcon sx={{ fontSize: 20, color: 'var(--c1-dark, #2c8db7)' }} />
          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '1rem', color: 'text.primary' }}>
            Pending Notes
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ fontSize: '0.82rem', color: 'text.secondary', lineHeight: 1.5 }}>
          {_fallbackFromToday
            ? `No new appointments to document today, but you have ${total_count} pending ${total_count === 1 ? 'note' : 'notes'} across ${patientsWithPending} ${patientsWithPending === 1 ? 'client' : 'clients'}.`
            : `${total_count} ${total_count === 1 ? 'session' : 'sessions'} with pending notes across ${patientsWithPending} ${patientsWithPending === 1 ? 'client' : 'clients'}.`}
        </Typography>

        {/* Segmented progress bar: signed (green) + drafted (amber) */}
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem' }}>
              Progress
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.72rem', color: 'text.secondary' }}>
              {signedPct}% done{draftedPct > 0 ? ` \u00b7 ${draftedPct}% drafted` : ''}
            </Typography>
          </Box>
          {/* Stacked progress bar */}
          <Box sx={{ position: 'relative', height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
            <Box
              sx={{
                position: 'absolute', left: 0, top: 0, height: '100%',
                width: `${signedPct}%`,
                bgcolor: 'var(--c2, #66b32b)',
                borderRadius: 3,
                transition: 'width 0.3s ease',
              }}
            />
            <Box
              sx={{
                position: 'absolute', left: `${signedPct}%`, top: 0, height: '100%',
                width: `${draftedPct}%`,
                bgcolor: 'var(--c4-amber, #f59e0b)',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, mt: 0.75 }}>
            {notStartedCount > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                <strong>{notStartedCount}</strong> not started
              </Typography>
            )}
            {draftedCount > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'var(--c4-amber, #f59e0b)' }}>
                <strong>{draftedCount}</strong> drafted
              </Typography>
            )}
            {signedCount > 0 && (
              <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'var(--c2, #66b32b)' }}>
                <strong>{signedCount}</strong> signed
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {/* 2. HIPAA toggle + section header */}
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'var(--c1-dark, #2c8db7)',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Sessions to Document
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={showFullNames}
              onChange={(e) => setShowFullNames(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'var(--c1, #57bde4)',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  bgcolor: 'var(--c1-light, #cfeefd)',
                },
              }}
            />
          }
          label={
            <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
              {showFullNames ? 'Names' : 'Initials'}
            </Typography>
          }
          sx={{ m: 0, gap: 0.25 }}
          labelPlacement="start"
        />
      </Box>

      {/* 3. Ordering controls and per-patient narrative cards */}
      <Box sx={{ px: 2, pt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <FormControl size="small" fullWidth>
          <InputLabel id="pending-notes-sort-label">Show sessions by</InputLabel>
          <Select
            labelId="pending-notes-sort-label"
            value={sortOrder}
            label="Show sessions by"
            onChange={(event) => setSortOrder(event.target.value as typeof sortOrder)}
            sx={{ fontSize: '0.8rem' }}
          >
            <MenuItem value="date_asc">Date, oldest first</MenuItem>
            <MenuItem value="date_desc">Date, newest first</MenuItem>
            <MenuItem value="client">Client, then date</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ px: 2, py: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {sortOrder === 'client'
          ? sortedPatients.map((patient) => (
            <PatientNarrativeCard
              key={patient.patient_id}
              patient={patient}
              showFullNames={showFullNames}
              currentEncounterId={currentEncounter?.id}
              onNoteClick={onNoteClick}
            />
          ))
          : dateGroups.map((group) => (
            <DateGroup
              key={group.date}
              date={group.date}
              patients={group.patients}
              showFullNames={showFullNames}
              currentEncounterId={currentEncounter?.id}
              onNoteClick={onNoteClick}
            />
          ))}
      </Box>
    </Box>
  );
}

function DateGroup({
  date,
  patients,
  showFullNames,
  currentEncounterId,
  onNoteClick,
}: {
  date: string;
  patients: PendingNotesBriefingProps['guidedWorkflowData']['notes'];
  showFullNames: boolean;
  currentEncounterId?: string;
  onNoteClick: (encounterId: string) => void;
}) {
  const hasCurrentSession = patients.some(patient =>
    patient.sessions.some(session => session.encounter_id === currentEncounterId),
  );
  const [expanded, setExpanded] = useState(hasCurrentSession);

  return (
    <Box sx={{ border: '1px solid var(--tile-border, #e5e7eb)', borderRadius: 1, overflow: 'hidden', mb: 0.5 }}>
      <Box
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 1,
          cursor: 'pointer',
          bgcolor: 'rgba(0,0,0,0.02)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
          {date}
        </Typography>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ px: 1, py: 0.5 }}>
          {patients.map(patient => (
            <PatientNarrativeCard
              key={`${date}-${patient.patient_id}`}
              patient={patient}
              showFullNames={showFullNames}
              currentEncounterId={currentEncounterId}
              onNoteClick={onNoteClick}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/*  Skeleton loader shown while data is loading                        */
/* ------------------------------------------------------------------ */

function PendingNotesSkeleton() {
  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Skeleton variant="text" width="60%" height={24} />
      <Skeleton variant="text" width="90%" height={16} sx={{ mt: 1 }} />
      <Skeleton variant="rectangular" height={6} sx={{ mt: 1.5, borderRadius: 3 }} />
      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[1, 2, 3].map(i => (
          <Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
        ))}
      </Box>
    </Box>
  );
}
